import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getConsent, setConsent, startTracking, subscribeConsent, trackPageview, trackingAllowed } from './tracker';
import './analytics.css';

type BannerState = 'hidden' | 'shown' | 'reopened';

/**
 * Mounted once in the public layout. Sends a pageview per route change and renders the cookie banner.
 * The banner is decided after mount, so prerendered HTML and the hydration pass contain nothing.
 */
export function Analytics() {
  const { pathname } = useLocation();
  const [banner, setBanner] = useState<BannerState>('hidden');

  useEffect(() => {
    if (!trackingAllowed()) return;
    setBanner(getConsent() ? 'hidden' : 'shown');
    const unsubscribe = subscribeConsent((consent, reopened) => setBanner(consent ? 'hidden' : reopened ? 'reopened' : 'shown'));
    const stop = startTracking();
    return () => { unsubscribe(); stop(); };
  }, []);

  useEffect(() => { trackPageview(pathname); }, [pathname]);

  return banner === 'hidden' ? null : <CookieBanner focusOnShow={banner === 'reopened'} />;
}

function CookieBanner({ focusOnShow }: { focusOnShow: boolean }) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  const bannerRef = useRef<HTMLElement>(null);
  // Only a banner the visitor asked for (privacy page settings) takes focus; the first one stays unobtrusive.
  useEffect(() => { if (focusOnShow) acceptRef.current?.focus(); }, [focusOnShow]);
  // Space the banner takes at the bottom of the viewport: fixed bottom-left widgets (the contest burst) lift above it.
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--cookie-banner-offset', `${Math.max(0, Math.ceil(window.innerHeight - banner.getBoundingClientRect().top))}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--cookie-banner-offset');
    };
  }, []);
  return <section ref={bannerRef} className="cookie-banner" role="region" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-text">
    <div className="cookie-banner__inner">
      <p className="cookie-banner__copy">
        <strong id="cookie-banner-title">Cookie и статистика</strong>{' '}
        <span id="cookie-banner-text">Мы используем cookie и собственную систему статистики на нашем сервере, чтобы понимать, какие страницы полезны, и улучшать сайт. Данные не передаются третьим лицам.</span>{' '}
        <Link to="/privacy#cookies">Подробнее</Link>
      </p>
      <div className="cookie-banner__actions">
        <button ref={acceptRef} type="button" className="cookie-banner__button cookie-banner__button--accept" onClick={() => setConsent('accepted')}>Принять</button>
        <button type="button" className="cookie-banner__button" onClick={() => setConsent('declined')}>Отклонить</button>
      </div>
    </div>
  </section>;
}
